import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../configurations/global.config';
import { AuthContext } from '../contexts/user-context/user-context';
import { fetchBranchRecommendation } from '../api/orders';
import { BranchRecommendationResponse } from '../interfaces/delivery-company/orders';
import { BranchType } from '../interfaces/delivery-company/branches';
import { Workflow } from '../enums/orders';

interface UseBranchRecommendationParams {
  workflow?: Workflow;
  customerLocationId?: number | string | null;
  merchantLocationId?: number | string | null;
  enabled?: boolean; // Whether to auto-fetch recommendations
}

interface UseBranchRecommendationResult {
  recommendation: BranchRecommendationResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  allBranches: BranchType[];
  isLoadingBranches: boolean;
}

/**
 * Custom hook for fetching branch recommendations with debouncing
 * @param params - Parameters for branch recommendation
 * @returns Branch recommendation data and loading state
 */
export const useBranchRecommendation = ({
  workflow,
  customerLocationId,
  merchantLocationId,
  enabled = true,
}: UseBranchRecommendationParams): UseBranchRecommendationResult => {
  const { config } = useContext(AuthContext);
  
  const [recommendation, setRecommendation] = useState<BranchRecommendationResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  
  const [allBranches, setAllBranches] = useState<BranchType[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState<boolean>(false);
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fetch all branches once on mount
  useEffect(() => {
    const fetchBranches = async () => {
      setIsLoadingBranches(true);
      try {
        const response = await axios.get<{ count: number; results: BranchType[] }>(
          `${API_ENDPOINT}/api/v1/branches/?page_size=100`,
          config
        );
        setAllBranches(response.data.results);
      } catch (err) {
        console.error('Failed to fetch branches:', err);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    
    fetchBranches();
  }, [config]);
  
  // Manual refetch function (doesn't depend on location IDs)
  const refetch = useCallback(async () => {
    if (!workflow || !enabled) {
      return;
    }
    
    // Determine which location is relevant based on workflow
    const isStandardWorkflow = workflow === Workflow.STANDARD;
    const relevantLocationId = isStandardWorkflow ? merchantLocationId : customerLocationId;
    
    // Only fetch if we have the relevant location
    if (!relevantLocationId) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchBranchRecommendation(
        {
          workflow,
          customer_location_id: !isStandardWorkflow && customerLocationId ? Number(customerLocationId) : null,
          merchant_location_id: isStandardWorkflow && merchantLocationId ? Number(merchantLocationId) : null,
        },
        config
      );
      setRecommendation(result);
    } catch (err) {
      console.error('Failed to fetch branch recommendation:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [workflow, customerLocationId, merchantLocationId, config, enabled]);
  
  // Debounced effect for STANDARD workflow (only watches merchantLocationId)
  useEffect(() => {
    if (!enabled || workflow !== Workflow.STANDARD) return;
    
    // Clear recommendation if merchant location is not provided
    if (!merchantLocationId) {
      setRecommendation(null);
      setIsLoading(false);
      return;
    }
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for debounced fetch (400ms)
    debounceTimerRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await fetchBranchRecommendation(
          {
            workflow: Workflow.STANDARD,
            customer_location_id: null,
            merchant_location_id: merchantLocationId ? Number(merchantLocationId) : null,
          },
          config
        );
        setRecommendation(result);
      } catch (err) {
        console.error('Failed to fetch branch recommendation:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    }, 400);
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [workflow, merchantLocationId, enabled, config]);
  
  // Debounced effect for RETURN workflow (only watches customerLocationId)
  useEffect(() => {
    if (!enabled || workflow !== Workflow.RETURN) return;
    
    // Clear recommendation if customer location is not provided
    if (!customerLocationId) {
      setRecommendation(null);
      setIsLoading(false);
      return;
    }
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for debounced fetch (400ms)
    debounceTimerRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await fetchBranchRecommendation(
          {
            workflow: Workflow.RETURN,
            customer_location_id: customerLocationId ? Number(customerLocationId) : null,
            merchant_location_id: null,
          },
          config
        );
        setRecommendation(result);
      } catch (err) {
        console.error('Failed to fetch branch recommendation:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    }, 400);
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [workflow, customerLocationId, enabled, config]);
  
  return {
    recommendation,
    isLoading,
    error,
    refetch,
    allBranches,
    isLoadingBranches,
  };
};

